import type {
  ContainerStyle,
  LayoutStyle,
  PanfletoContentConfig,
  PriceBoxStyle,
  TextStyle,
} from '../../../shared/types';
import {
  clonePanfletoContentDeep,
  mergePanfletoContent,
  mergePanfletoContentPreserving,
} from '../../banners/utils/panfletoContentUtils';

/** Identificador do JSON exportado só com dados do editor de panfleto (mesmos botões do estilo geral). */
export const PANFLETO_STYLE_EXPORT_KIND = 'panfleto-style' as const;

export type PanfletoStyleExportKind = typeof PANFLETO_STYLE_EXPORT_KIND;

/**
 * Bases de texto usadas em `getTextStyle` + `mergePanfletoFieldStyle` no cartaz (PromoCard / StylePreview).
 * Campos opcionais só entram no JSON se existirem no layout.
 */
export interface PanfletoStyleTextStylesSlice {
  price: TextStyle;
  productName: TextStyle;
  unit: TextStyle;
  category?: TextStyle;
  description?: TextStyle;
  validUntil?: TextStyle;
}

/**
 * Tudo que afeta a aparência do panfleto na tela:
 * - `panfletoContent`: editor dedicado (textos, fundo do cartaz, overrides por campo);
 * - `textStyles`: bases `LayoutStyle` usadas quando não há override em `styles`;
 * - `productContainer` / `priceBox`: card, fundo, imagem, bordas (StylePreview / PromoCard);
 * - `typography`: redundante com `textStyles.fontFamily`, mantido para compat. com JSON antigos.
 */
export interface PanfletoStyleExportFile {
  exportKind: PanfletoStyleExportKind;
  version: 1;
  panfletoContent: PanfletoContentConfig;
  typography: {
    priceFontFamily: string;
    textFontFamily: string;
  };
  /** Desde exportações completas: fundo do card, imagem, paddings, etc. */
  productContainer?: ContainerStyle;
  textStyles?: PanfletoStyleTextStylesSlice;
  priceBox?: PriceBoxStyle;
}

function mergeTextPreserve(
  current: TextStyle | undefined,
  patch: Partial<TextStyle>
): TextStyle {
  return {
    color: '#000000',
    fontSize: 16,
    fontFamily: 'Arial, sans-serif',
    ...current,
    ...patch,
  };
}

/** Preserva `backgroundImage` (base64) por referência própria no clone. */
function cloneProductContainer(pc: ContainerStyle | undefined): ContainerStyle {
  if (!pc) {
    return {};
  }
  return {
    ...pc,
    backgroundImage: pc.backgroundImage,
  };
}

function clonePriceBox(pb: PriceBoxStyle | undefined): PriceBoxStyle | undefined {
  if (!pb) return undefined;
  return { ...pb };
}

function buildTextStylesSlice(layoutStyle: LayoutStyle): PanfletoStyleTextStylesSlice {
  const slice: PanfletoStyleTextStylesSlice = {
    price: { ...layoutStyle.price },
    productName: { ...layoutStyle.productName },
    unit: { ...layoutStyle.unit },
  };
  if (layoutStyle.category) slice.category = { ...layoutStyle.category };
  if (layoutStyle.description) slice.description = { ...layoutStyle.description };
  if (layoutStyle.validUntil) slice.validUntil = { ...layoutStyle.validUntil };
  return slice;
}

function applyTextStylesSlice(base: LayoutStyle, slice: PanfletoStyleTextStylesSlice): Partial<LayoutStyle> {
  const out: Partial<LayoutStyle> = {
    price: { ...base.price, ...slice.price },
    productName: { ...base.productName, ...slice.productName },
    unit: { ...base.unit, ...slice.unit },
  };
  if (slice.category !== undefined) {
    out.category = base.category ? { ...base.category, ...slice.category } : { ...slice.category };
  }
  if (slice.description !== undefined) {
    out.description = base.description
      ? { ...base.description, ...slice.description }
      : { ...slice.description };
  }
  if (slice.validUntil !== undefined) {
    out.validUntil = base.validUntil
      ? { ...base.validUntil, ...slice.validUntil }
      : { ...slice.validUntil };
  }
  return out;
}

export function isPanfletoStyleExportFile(data: unknown): data is PanfletoStyleExportFile {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) return false;
  const o = data as Record<string, unknown>;
  if (o.exportKind !== PANFLETO_STYLE_EXPORT_KIND) return false;
  if (o.version !== 1) return false;
  if (o.panfletoContent !== undefined && (typeof o.panfletoContent !== 'object' || o.panfletoContent === null)) {
    return false;
  }
  const typo = o.typography;
  if (typo !== undefined && (typeof typo !== 'object' || typo === null || Array.isArray(typo))) {
    return false;
  }
  const pc = o.productContainer;
  if (pc !== undefined && (typeof pc !== 'object' || pc === null || Array.isArray(pc))) {
    return false;
  }
  const ts = o.textStyles;
  if (ts !== undefined && (typeof ts !== 'object' || ts === null || Array.isArray(ts))) {
    return false;
  }
  const pb = o.priceBox;
  if (pb !== undefined && (typeof pb !== 'object' || pb === null || Array.isArray(pb))) {
    return false;
  }
  return true;
}

/** Monta o objeto serializado para exportação (tela panfleto). */
export function buildPanfletoStyleExportPayload(layoutStyle: LayoutStyle): PanfletoStyleExportFile {
  const merged = mergePanfletoContent(layoutStyle.panfletoContent);
  const textStyles = buildTextStylesSlice(layoutStyle);
  return {
    exportKind: PANFLETO_STYLE_EXPORT_KIND,
    version: 1,
    panfletoContent: clonePanfletoContentDeep(merged),
    typography: {
      priceFontFamily: String(layoutStyle.price?.fontFamily ?? '').trim(),
      textFontFamily: String(layoutStyle.productName?.fontFamily ?? '').trim(),
    },
    productContainer: cloneProductContainer(layoutStyle.productContainer),
    textStyles,
    priceBox: clonePriceBox(layoutStyle.priceBox),
  };
}

/**
 * Aplica arquivo {@link PanfletoStyleExportFile} sobre o layout atual.
 * JSON antigo (só `typography`): ajusta só fontes; mantém `productContainer` / textos da base.
 * JSON novo (`textStyles` + `productContainer`): restaura também card e bases de texto.
 */
export function mergePanfletoStyleImportIntoLayout(
  base: LayoutStyle,
  file: PanfletoStyleExportFile
): LayoutStyle {
  const nextContent = mergePanfletoContentPreserving(base.panfletoContent, file.panfletoContent);

  const next: LayoutStyle = {
    ...base,
    panfletoContent: nextContent,
  };

  if (file.productContainer) {
    next.productContainer = cloneProductContainer(file.productContainer);
  }

  if (file.priceBox !== undefined) {
    next.priceBox = file.priceBox ? clonePriceBox(file.priceBox) : undefined;
  }

  if (file.textStyles) {
    Object.assign(next, applyTextStylesSlice(base, file.textStyles));
  } else {
    const priceFf = file.typography?.priceFontFamily?.trim();
    const textFf = file.typography?.textFontFamily?.trim();
    next.price = mergeTextPreserve(base.price, priceFf ? { fontFamily: priceFf } : {});
    next.productName = mergeTextPreserve(base.productName, textFf ? { fontFamily: textFf } : {});
    next.category = mergeTextPreserve(base.category, textFf ? { fontFamily: textFf } : {});
    next.description = mergeTextPreserve(base.description, textFf ? { fontFamily: textFf } : {});
    next.unit = mergeTextPreserve(base.unit, textFf ? { fontFamily: textFf } : {});
    next.validUntil = mergeTextPreserve(base.validUntil, textFf ? { fontFamily: textFf } : {});
  }

  return next;
}
